import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import './PaymentValidationPage.css';

interface CreditPurchase {
  id: string;
  user_id: string;
  amount: number;
  credits: number;
  status: 'pending' | 'approved' | 'rejected';
  payment_proof_url?: string;
  created_at: string;
  profiles?: {
    email: string;
    full_name?: string;
  };
}

const PaymentValidationPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<CreditPurchase[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [loading, setLoading] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<CreditPurchase | null>(null);
  const [showProofModal, setShowProofModal] = useState(false);

  useEffect(() => {
    if (user) {
      checkAdminAccess();
      fetchPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  const checkAdminAccess = async () => {
    // In production, implement proper admin role checking
    // For now, we'll use a simple email check
    const adminEmails = ['admin@rifabr.com', 'fabiorizzo@gmail.com'];
    
    if (!user || !adminEmails.includes(user.email || '')) {
      navigate('/');
      return;
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('credit_purchases')
        .select(`
          *,
          profiles:user_id (
            email,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPurchases(data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (purchaseId: string) => {
    if (!window.confirm('Confirma a aprovação deste pagamento?')) return;

    try {
      const { error } = await supabase
        .from('credit_purchases')
        .update({ status: 'approved' })
        .eq('id', purchaseId);

      if (error) throw error;

      alert('Pagamento aprovado! Os créditos foram adicionados à conta do usuário.');
      fetchPurchases();
    } catch (error) {
      console.error('Error approving payment:', error);
      alert('Erro ao aprovar pagamento.');
    }
  };

  const handleReject = async (purchaseId: string) => {
    const reason = prompt('Motivo da rejeição:');
    if (!reason) return;

    try {
      const { error } = await supabase
        .from('credit_purchases')
        .update({ 
          status: 'rejected',
          // In production, add a rejection_reason field
        })
        .eq('id', purchaseId);

      if (error) throw error;

      alert('Pagamento rejeitado.');
      fetchPurchases();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Erro ao rejeitar pagamento.');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pt-BR');
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      pending: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-danger'
    };

    const statusLabels: Record<string, string> = {
      pending: 'Pendente',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  return (
    <div className="payment-validation-page">
      <div className="container">
        <div className="page-header">
          <h1>Validação de Pagamentos</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Voltar ao Dashboard
          </button>
        </div>

        <div className="filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Aguardando Validação
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pendentes
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Aprovados
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejeitados
          </button>
        </div>

        {loading ? (
          <div className="loading">Carregando...</div>
        ) : purchases.length === 0 ? (
          <div className="no-data">Nenhum pagamento encontrado.</div>
        ) : (
          <div className="purchases-grid">
            {purchases.map((purchase) => (
              <div key={purchase.id} className="purchase-card">
                <div className="purchase-header">
                  <div className="user-info">
                    <strong>{purchase.profiles?.full_name || 'Usuário'}</strong>
                    <span className="email">{purchase.profiles?.email}</span>
                  </div>
                  {getStatusBadge(purchase.status)}
                </div>

                <div className="purchase-details">
                  <div className="detail-row">
                    <span className="label">Valor:</span>
                    <span className="value">{formatCurrency(purchase.amount)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Créditos:</span>
                    <span className="value">{purchase.credits}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Data:</span>
                    <span className="value">{formatDate(purchase.created_at)}</span>
                  </div>
                </div>

                <div className="purchase-actions">
                  {purchase.payment_proof_url && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setSelectedPurchase(purchase);
                        setShowProofModal(true);
                      }}
                    >
                      Ver Comprovante
                    </button>
                  )}
                  
                  {purchase.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleApprove(purchase.id)}
                      >
                        Aprovar
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleReject(purchase.id)}
                      >
                        Rejeitar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Proof Modal */}
      {showProofModal && selectedPurchase && (
        <div className="modal-overlay" onClick={() => setShowProofModal(false)}>
          <div className="modal-content proof-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowProofModal(false)}
            >
              ×
            </button>
            <h2>Comprovante de Pagamento</h2>
            <div className="proof-container">
              <img
                src={selectedPurchase.payment_proof_url}
                alt="Comprovante"
                className="proof-image"
              />
            </div>
            <div className="proof-details">
              <p><strong>Usuário:</strong> {selectedPurchase.profiles?.email}</p>
              <p><strong>Valor:</strong> {formatCurrency(selectedPurchase.amount)}</p>
              <p><strong>Data:</strong> {formatDate(selectedPurchase.created_at)}</p>
            </div>
            <div className="modal-actions">
              {selectedPurchase.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      handleApprove(selectedPurchase.id);
                      setShowProofModal(false);
                    }}
                  >
                    Aprovar Pagamento
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      handleReject(selectedPurchase.id);
                      setShowProofModal(false);
                    }}
                  >
                    Rejeitar Pagamento
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentValidationPage;